import { Path } from './Path.js';

/**
 * Get a list of actions which would be triggered by the data contained in the update object.
 * @param {string} rootPath - The path of the firebase reference
 * @param {Object} update - A Firebase update object
 * @param {Object} actions - The action definitions from models
 */
let sessionActions = {};
export function GetTriggeredActions(rootPath, update, actions) {
  // If update is a primitive value, turn it into an update object.
  if (!Path.isObject(update)) update = { '/': update };
  // Only perform action tracking if models are loaded.
  if (Array.isArray(actions)) {
    // Make matches an object and set based on path so duplicates are not an issue.
    let triggeredActions = {};
    // Flatten update and rebuild as a whole object.
    let flattenedUpdate = Path.flattenObject(update, '/');
    // Make an array from flattened update entries where all paths are mapped to absolute paths
    let flattenedUpdateArray = Object.entries(flattenedUpdate).map(([path, value]) => ({ path: Path.join([rootPath, path], '/'), value }));
    // Rebuild an absolute path version of the update object.
    let rebuiltUpdate = {};
    flattenedUpdateArray.forEach((entry) => Path.set(rebuiltUpdate, entry.path, entry.value, '/'));
    // Compare each abs path against action tracking rules.
    flattenedUpdateArray.forEach((entry) => {
      actions.forEach((action) => {
        // If the action is disabled, skip it
        if (action._disabled == true) return;
        // If this update path starts with the path to the attribute, then we have a match.
        if (Path.startsWith(entry.path, action.path, '/') && (!action.sub_attribute || entry.path?.endsWith(action.sub_attribute))) {
          // Get the value of the jobId and nodeId wildcards.
          let jobId, nodeId;
          let wildcardVals = ([jobId, nodeId] = Path.getWildcardValues(entry.path, action.path, '/'));
          // If the action should only be triggered once per session and already was, skip it
          if (Path.get(action, 'behaviors.trigger_once_per_session')) {
            if (sessionActions[`${action.$key}:${jobId}`]) return;
            sessionActions[`${action.$key}:${jobId}`] = true;
          }
          // Use these values to build the abs path of this attribute and use that path to get the value at that location from the rebuild update object.
          let attributeValues = Path.get(rebuiltUpdate, Path.setWildcardValues(action.path, wildcardVals, '/'), '/');
          // If the action type is job, format the value as an object
          if (action.type == 'job' && !action.sub_attribute) attributeValues = { a: attributeValues };
          // Get the key of the entry value from its path.
          const endKey = entry.path?.split('/')?.at(-1) || '';
          let value = attributeValues?.[endKey] || Object.values(attributeValues || {})[0];
          if (action.sub_attribute) value = Path.get(value, action.sub_attribute, '/');
          let triggerSatisfied = false;
          if (value) {
            if (action.trigger_type == 'any') {
              triggerSatisfied = ![null, undefined, ''].includes(value);
            } else if (action.trigger_list) {
              triggerSatisfied = Object.values(action.trigger_list).some((item) => {
                if (typeof item === 'string') item = item.toLowerCase();
                return item == value.toLowerCase();
              });
            } else {
              let trigger = action.trigger;
              if (typeof trigger === 'string') trigger = trigger.toLowerCase();
              triggerSatisfied = (typeof value === 'string' ? value.toLowerCase() : value) == trigger;
            }
          }
          // Log this match.
          if (triggerSatisfied) triggeredActions[`${jobId}.${nodeId}.${action.$key}`] = { ...action, jobId, nodeId, value };
        }
      });
    });
    // Convert matches to an array.
    triggeredActions = Object.values(triggeredActions);
    return triggeredActions;
  }
}

/** @import { ClientOrAdminFirestore } from './types/ClientOrAdminFirebase.js' */

/**
 * Overwrites an existing action with a new action in Firestore, optionally transferring feedback and status.
 *
 * @param {Object} oldAction - The existing action object to be overwritten.
 * @param {Object} newAction - The new action object to be saved.
 * @param {string} companyId - The ID of the company whose action is being overwritten.
 * @param {ClientOrAdminFirestore} firestore - The Firestore instance to use for database operations.
 * @returns {Promise<void>} Resolves when the overwrite operation is complete.
 */
export async function OverwriteAction(oldAction, newAction, companyId, firestore) {
  if (!oldAction) throw new Error('`oldAction` is required.');
  if (!newAction) throw new Error('`newAction` is required.');
  if (!companyId) throw new Error('`companyId` is required.');
  if (!firestore) throw new Error('`firestore` is required.');

  const newActionDocRef = firestore.collection(`companies/${companyId}/action_tracking`).doc();
  const feedbackRef = oldAction?.feedback_ref;
  // If feedback exists, move it to the new action
  if (feedbackRef) feedbackRef.update({ action_ref: newActionDocRef });
  // Delete the old action
  await firestore.doc(`companies/${companyId}/action_tracking/${oldAction.$key}`).delete();

  if (feedbackRef) {
    newAction.feedback_ref = feedbackRef;
    newAction.status = oldAction.status;
  }
  newActionDocRef.set(newAction);
}
